import React, { useCallback, useState } from 'react';
import css from './ListingsTable.module.css';
import { Icons, SecondaryButtonInline } from '../../../components';
import { FormattedMessage } from 'react-intl';
import {
  closeListing,
  discardDraft,
  openListing,
  publishDraft,
  queryOwnListings,
} from '../ShopSettingsPage.duck';
import { isArrayLength } from '../../../util/genericHelpers';
import { ListingState } from '../../../util/enums';
import { showErrorToast, showSuccessToast } from '../../../util/toast';
const RESULT_PAGE_SIZE = 30;
/**
 * Action bar component for managing selected listings.
 * @param {Set} selectedItems - Set of selected listing IDs
 * @param {string} activeState - Current active state (e.g., DRAFT, PUBLISHED)
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} config - Configuration object for actions
 */
const ActionBarButtons = ({
  selectedItems,
  setSelectedItems,
  activeState,
  dispatch,
  config,
  openModal,
  isShopifyAccountPage,
}) => {
  const [discardInProgress, setDiscardInProgress] = useState(false);
  const [publishInProgress, setPublishInProgress] = useState(false);
  // Convert Set to Array for iteration
  const selectedIds = Array.from(selectedItems);

  // Common query for refreshing listings
  const refreshListings = useCallback(() => {
    dispatch(
      queryOwnListings({
        config,
        page: 1,
        perPage: RESULT_PAGE_SIZE,
        fetchAll: true,
      })
    );
  }, [dispatch, config, isShopifyAccountPage]);

  // Async handler for discarding drafts
  const handleDiscard = useCallback(async () => {
    if (activeState === ListingState.DRAFT && isArrayLength(selectedIds)) {
      setDiscardInProgress(true);
      try {
        await Promise.all(selectedIds.map(id => dispatch(discardDraft(id, config))));
        refreshListings();
        showSuccessToast('Draft listing discarded!');
      } catch (error) {
        showErrorToast('Failed to discard listing.');
      } finally {
        setDiscardInProgress(false);
        setSelectedItems([]);
      }
    } else if (activeState === ListingState.PUBLISHED && isArrayLength(selectedIds)) {
      setDiscardInProgress(true);

      try {
        await Promise.all(selectedIds.map(id => dispatch(closeListing(id, config))));
        refreshListings();
        showSuccessToast('Listing closed!', '#ff0000');
      } catch (error) {
        showErrorToast('Failed to close listing.');
      } finally {
        setDiscardInProgress(false);
        setSelectedItems([]);
      }
    }
  }, [activeState, selectedIds, dispatch, config]);

  // Async handler for publishing drafts
  const handlePublish = useCallback(async () => {
    if (activeState === ListingState.DRAFT && isArrayLength(selectedIds)) {
      setPublishInProgress(true);
      try {
        await Promise.all(selectedIds.map(id => dispatch(publishDraft(id, config))));
        refreshListings();
        showSuccessToast('Draft listing published!');
      } catch (error) {
        showErrorToast('Failed to publish listing.');
      } finally {
        setPublishInProgress(false);
        setSelectedItems([]);
      }
    }
  }, [activeState, selectedIds, dispatch, config]);

  // Async handler for publishing drafts
  const handleOpen = useCallback(async () => {
    if (activeState === ListingState.CLOSED && isArrayLength(selectedIds)) {
      setPublishInProgress(true);
      try {
        await Promise.all(selectedIds.map(id => dispatch(openListing(id, config))));
        refreshListings();
        showSuccessToast('Listing opened!');
      } catch (error) {
        showErrorToast('Failed to open listing.');
      } finally {
        setPublishInProgress(false);
        setSelectedItems([]);
      }
    }
  }, [activeState, selectedIds, dispatch, config]);

  // Render action buttons based on active state
  const renderActionButtons = () => {
    if (!isArrayLength(selectedIds)) return null;

    if (activeState === ListingState.DRAFT) {
      return (
        <>
          <SecondaryButtonInline type="button" onClick={openModal}>
            <Icons name="pencil" className={css.actionIcon} />
            <FormattedMessage id="ShopSettingsForm.edit" />
          </SecondaryButtonInline>
          <SecondaryButtonInline
            type="button"
            onClick={handlePublish}
            inProgress={publishInProgress}
          >
            <Icons name="upload" className={css.actionIcon} />
            <FormattedMessage id="ShopSettingsForm.publish" />
          </SecondaryButtonInline>
          <SecondaryButtonInline
            type="button"
            onClick={handleDiscard}
            inProgress={discardInProgress}
          >
            <Icons name="trash" className={css.actionIcon} />
            <FormattedMessage id="ShopSettingsForm.discard" />
          </SecondaryButtonInline>
        </>
      );
    }

    if (activeState === ListingState.PUBLISHED) {
      return (
        <>
          <SecondaryButtonInline type="button" onClick={openModal}>
            <Icons name="pencil" className={css.actionIcon} />
            <FormattedMessage id="ShopSettingsForm.edit" />
          </SecondaryButtonInline>
          <SecondaryButtonInline
            type="button"
            onClick={handleDiscard}
            inProgress={discardInProgress}
          >
            <Icons name="trash" className={css.actionIcon} />
            <FormattedMessage id="ShopSettingsForm.discard" />
          </SecondaryButtonInline>
        </>
      );
    }
    if (activeState === ListingState.CLOSED) {
      return (
        <>
          <SecondaryButtonInline type="button" onClick={openModal}>
            <Icons name="pencil" className={css.actionIcon} />
            <FormattedMessage id="ShopSettingsForm.edit" />
          </SecondaryButtonInline>
          <SecondaryButtonInline type="button" onClick={handleOpen} inProgress={publishInProgress}>
            <Icons name="openLock" className={css.actionIcon} />
            <FormattedMessage id="ShopSettingsForm.open" />
          </SecondaryButtonInline>
        </>
      );
    }

    return null;
  };

  return (
    <div className={css.actionBar}>
      <div className={css.selectedCount}>
        {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''}{' '}
        <FormattedMessage id="ShopSettingsForm.selected" />
      </div>
      <div className={css.actionButtons}>{renderActionButtons()}</div>
    </div>
  );
};

export default ActionBarButtons;
